import React, { useState } from 'react'
import { useConfiguration } from '../contexts/ConfigurationContext'
import { Save, Download, RotateCcw, CheckCircle } from 'lucide-react'

function ConfigurationActions() {
  const { state, dispatch, saveConfiguration, exportConfiguration } = useConfiguration()
  const [configName, setConfigName] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleSave = async () => {
    if (!configName.trim()) {
      alert('Please enter a configuration name')
      return
    }

    setSaving(true)
    try {
      await saveConfiguration(configName.trim())
      setSaved(true)
      setConfigName('')
      setTimeout(() => setSaved(false), 2000)
    } catch (error) {
      alert('Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  const handleExport = () => {
    if (!state.selectedProvider) {
      alert('Please select a provider first')
      return
    }
    exportConfiguration()
  }

  const handleReset = () => {
    if (confirm('Are you sure you want to reset the configuration?')) {
      dispatch({ type: 'RESET_CONFIGURATION' })
    }
  }

  const isConfigurationValid = state.selectedProvider && state.costData

  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-4">
        <Save className="h-4 w-4 text-primary-600" />
        <h3 className="text-base font-semibold text-gray-900">
          Actions
        </h3>
      </div>

      <div className="space-y-4">
        {/* Save Configuration */}
        <div>
          <label htmlFor="config-name" className="block text-sm font-medium text-gray-700 mb-2">
            Save Configuration
          </label>
          <div className="flex space-x-2">
            <input
              id="config-name"
              type="text"
              placeholder="Configuration name..."
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
              className="flex-1 input-field"
              disabled={!isConfigurationValid}
            />
            <button
              onClick={handleSave}
              disabled={saving || !isConfigurationValid || !configName.trim()}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                saved
                  ? 'bg-green-600 text-white'
                  : saving || !isConfigurationValid || !configName.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'btn-primary'
              }`}
            >
              {saved ? (
                <CheckCircle className="h-4 w-4" />
              ) : saving ? (
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
            </button>
          </div>
          {!isConfigurationValid && (
            <p className="text-xs text-gray-500 mt-1">
              Select a provider and configure resources to save
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleExport}
            disabled={!isConfigurationValid}
            className={`flex items-center justify-center space-x-2 py-2 px-4 rounded-lg font-medium transition-colors ${
              isConfigurationValid
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Download className="h-4 w-4" />
            <span>Export JSON</span>
          </button>

          <button
            onClick={handleReset}
            className="flex items-center justify-center space-x-2 py-2 px-4 rounded-lg font-medium bg-gray-600 hover:bg-gray-700 text-white transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Reset</span>
          </button>
        </div>

        {/* Quick Actions */}
        {isConfigurationValid && (
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <button
                onClick={() => {
                  setConfigName(`${state.selectedProvider.shortName}-${state.vcpus}vCPU-${state.ramGb}GB`)
                }}
                className="p-2 text-left bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                Auto-name config
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify({
                    provider: state.selectedProvider.name,
                    vcpus: state.vcpus,
                    ramGb: state.ramGb,
                    storageGb: state.storageGb,
                    monthlyCost: state.costData.monthly
                  }, null, 2))
                }}
                className="p-2 text-left bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                Copy to clipboard
              </button>
            </div>
          </div>
        )}

        {/* Configuration Summary */}
        {isConfigurationValid && (
          <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Current Configuration</h4>
            <div className="text-xs text-gray-600 space-y-1">
              <p>Provider: {state.selectedProvider.name}</p>
              <p>Resources: {state.vcpus} vCPU, {state.ramGb}GB RAM, {state.storageGb}GB storage</p>
              <p>Monthly Cost: ${state.costData.monthly.toFixed(2)}</p>
              <p>Instance Type: {state.instanceType}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ConfigurationActions 